import type { ReactNode } from "react";
import HermesDialog from "./HermesDialog";

/**
 * The frame every Hermes action card shares (Zeplin: Hermes_no of
 * Companies_bookmark / Hermes_actionbar): ChatShell renders the open card in
 * place of the composer, so it matches the composer's width. It always sits at
 * the bottom of the window, so dropdowns inside a card open upward
 * (menuPlacement="top") — downward they run off the screen.
 *
 *   title
 *   body (picker chip, fields)
 *   ─────────────────────────────
 *   footer note           Discard  [Primary]
 */
type ActionCardShellProps = {
  title: ReactNode;
  children: ReactNode;
  primaryLabel: string;
  busyLabel: string;
  busy: boolean;
  primaryDisabled?: boolean;
  onPrimary: () => void;
  onDiscard: () => void;
  // Shown on the left of the footer, opposite the buttons (e.g. the download cost).
  footerNote?: ReactNode;
  // Render as a dialog over the page instead of the inline card that replaces the
  // composer — same content and buttons. Used when an action is taken from the
  // results panel, which has no composer to replace.
  modal?: boolean;
};

export default function ActionCardShell({ title, children, primaryLabel, busyLabel, busy, primaryDisabled = false, onPrimary, onDiscard, footerNote, modal = false }: ActionCardShellProps) {
  if (modal) {
    return (
      <HermesDialog
        title={title}
        onClose={onDiscard}
        busy={busy}
        widthClassName="tw-max-w-2xl"
        footer={
          <>
            <div className="tw-min-w-0 tw-flex-1 tw-text-sm tw-text-gray-700">{footerNote}</div>
            <button type="button" onClick={onDiscard} disabled={busy} className="tw-h-9 tw-text-sm tw-font-medium tw-px-4 tw-rounded-lg tw-border-none tw-bg-white tw-shadow-[0_2px_4px_rgba(16,24,40,0.06)] tw-text-gray-900 hover:tw-bg-gray-50 tw-transition-colors tw-cursor-pointer disabled:tw-opacity-60 disabled:tw-cursor-not-allowed">
              Discard
            </button>
            <button type="button" onClick={onPrimary} disabled={busy || primaryDisabled} className="tw-h-9 tw-text-sm tw-font-medium tw-px-4 tw-rounded-lg tw-border-none tw-bg-primary-purple-600 tw-text-white hover:tw-bg-primary-purple-500 tw-transition-colors tw-cursor-pointer disabled:tw-opacity-60 disabled:tw-cursor-not-allowed">
              {busy ? busyLabel : primaryLabel}
            </button>
          </>
        }>
        {children}
      </HermesDialog>
    );
  }

  return (
    <div className="tw-max-w-3xl tw-mx-auto tw-w-full tw-rounded-2xl tw-border tw-border-primary-purple-500 tw-bg-white tw-px-[13px] tw-pt-[18px] tw-pb-[18px] tw-shadow-[0_0_0_2px_#E2D9FC]">
      <p className="tw-m-0 tw-text-sm tw-leading-5 tw-font-semibold tw-text-gray-900">{title}</p>

      {children}

      <div className="tw-mt-4 tw-flex tw-items-center tw-gap-3 tw-border-t tw-border-gray-200 tw-pt-2">
        <div className="tw-min-w-0 tw-flex-1 tw-text-sm tw-text-gray-700">{footerNote}</div>
        <button type="button" onClick={onDiscard} disabled={busy} className="tw-h-9 tw-text-sm tw-font-medium tw-px-4 tw-rounded-lg tw-border-none tw-bg-white tw-shadow-[0_2px_4px_rgba(16,24,40,0.06)] tw-text-gray-900 hover:tw-bg-gray-50 tw-transition-colors tw-cursor-pointer disabled:tw-opacity-60 disabled:tw-cursor-not-allowed">
          Discard
        </button>
        <button type="button" onClick={onPrimary} disabled={busy || primaryDisabled} className="tw-h-9 tw-text-sm tw-font-medium tw-px-4 tw-rounded-lg tw-border-none tw-bg-primary-purple-600 tw-text-white hover:tw-bg-primary-purple-500 tw-transition-colors tw-cursor-pointer disabled:tw-opacity-60 disabled:tw-cursor-not-allowed">
          {busy ? busyLabel : primaryLabel}
        </button>
      </div>
    </div>
  );
}

// Avatar chips for the first three selected companies.
const AVATAR_COLORS = ["tw-bg-[#EFE9FD] tw-text-[#5B21D6]", "tw-bg-[#E3F1EC] tw-text-[#1D7A55]", "tw-bg-[#FDEEE3] tw-text-[#B4561A]"];

/**
 * The heading of an action card opened from the bar beneath a table: how many records the answer's
 * result holds (all of them when the preview shows everything), as in the design. Cards opened from
 * ticked rows are modals headed by their action's own name instead.
 */
export function resultsHeading(count: number): string {
  return `The results contains total of ${count.toLocaleString()} ${count === 1 ? "record" : "records"}`;
}

type SelectedCompaniesChipProps = {
  companies: { key: string; name: string }[];
  selectedKeys: Set<string>;
  // Opens the full table to choose from every record. Given only when there are more
  // records than the answer's table shows.
  onCustomise?: () => void;
  // What the pill counts and calls them, when that is not the ticked companies (a download of the rows shown).
  count?: number;
  noun?: string;
};

/**
 * "LM F NC  N companies selected [Customise selection]". The selection is the rows
 * the user ticked in the answer's table (or the rows it shows); to change it they
 * discard and tick again, or — when the result is bigger than the preview — choose
 * from all of it in the full table.
 */
export function SelectedCompaniesChip({ companies, selectedKeys, onCustomise, count: shownCount, noun = "companies" }: SelectedCompaniesChipProps) {
  const selected = companies.filter((company) => selectedKeys.has(company.key));
  const count = shownCount ?? selected.length;
  const singularNoun = noun.endsWith("ies") ? `${noun.slice(0, -3)}y` : noun.replace(/s$/, "");

  return (
    <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
      <span className="tw-flex tw-w-fit tw-items-center tw-gap-2 tw-h-[38px] tw-rounded-full tw-border tw-border-[#E3E5EA] tw-bg-white tw-pl-1.5 tw-pr-3 tw-text-[13.5px] tw-font-semibold tw-text-[#1F2430]">
        <span className="tw-flex -tw-space-x-2" aria-hidden="true">
          {selected.slice(0, 3).map((company, index) => (
            <span key={company.key} className={`tw-flex tw-items-center tw-justify-center tw-w-[26px] tw-h-[26px] tw-rounded-full tw-border-2 tw-border-white tw-text-[10.5px] tw-font-semibold ${AVATAR_COLORS[index]}`}>
              {initials(company.name)}
            </span>
          ))}
        </span>
        {count.toLocaleString()} {count === 1 ? singularNoun : noun} selected
      </span>
      {onCustomise ? (
        <button type="button" onClick={onCustomise} className="tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-primary-purple-600 hover:tw-underline tw-cursor-pointer">
          Customise selection
        </button>
      ) : null}
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
}
